import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;
const ENGRAVING_ADDON_DEFAULT_HANDLE = 'engraving';
const ENGRAVING_ADDON_FALLBACK_HANDLES = [
  ENGRAVING_ADDON_DEFAULT_HANDLE,
  'engraving-upcharge',
  'engraving-upcharge-1',
  'engraving-addon',
];
const ENGRAVING_ADDON_CACHE_KEY = '__engraving-addon-variant-id__';
const engravingAddonVariantCache = new Map();
const linkedAddonVariantCache = new Map();
const LINKED_ADDON_SELECTION_RULES = {
  custom_charm_selection: {
    propertyName: 'properties[Charm selections]',
    label: 'Charm selections',
    allowedValues: ['C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7', 'C8', 'C9'],
  },
  natural_stone_beads: {
    propertyName: 'properties[Bead selections]',
    label: 'Bead selections',
    allowedValues: ['B1', 'B2'],
  },
};

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement | undefined} acceleratedCheckoutNotice - The accelerated checkout engraving notice element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    this.addEventListener('change', this.#onFormStateChange, { signal });
    this.addEventListener('click', this.#onAddonStepperClick, { signal });

    this.#syncEngravingDetailFields();
    this.#syncLinkedAddonSelectionFields();
    this.#syncAcceleratedCheckoutState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Shows an error in the product form error region.
   * @param {string} message
   */
  #showFormError(message) {
    const { addToCartTextError } = this.refs;
    if (!addToCartTextError) return;

    addToCartTextError.classList.remove('hidden');

    const textNode = addToCartTextError.childNodes[2];
    if (textNode) {
      textNode.textContent = message;
    } else {
      addToCartTextError.appendChild(document.createTextNode(message));
    }

    this.#setLiveRegionText(message);

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      if (!addToCartTextError) return;
      addToCartTextError.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * Whether engraving has been selected on the current form.
   * @returns {boolean}
   */
  #hasSelectedCartAddon() {
    const engravingSelected = Boolean(this.querySelector('[data-engraving-opt-in]:checked'));
    if (engravingSelected) return true;

    const linkedAddonQuantities = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('[data-linked-addon-quantity]')
    );
    return Array.from(linkedAddonQuantities).some((input) => (Number(input.value) || 0) > 0);
  }

  /**
   * Sync accelerated checkout visibility with current product form state.
   * Engraving uses a second hidden line item, which accelerated checkout cannot append.
   * @param {boolean} [isVariantAvailable=true]
   */
  #syncAcceleratedCheckoutState(isVariantAvailable = true) {
    const { acceleratedCheckoutButtonContainer, acceleratedCheckoutNotice } = this.refs;
    if (!acceleratedCheckoutButtonContainer && !acceleratedCheckoutNotice) return;

    const cartAddonSelected = this.#hasSelectedCartAddon();
    const canUseAcceleratedCheckout = isVariantAvailable && !cartAddonSelected;

    if (acceleratedCheckoutButtonContainer) {
      if (canUseAcceleratedCheckout) {
        acceleratedCheckoutButtonContainer.removeAttribute('hidden');
      } else {
        acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
      }
    }

    if (acceleratedCheckoutNotice) {
      if (isVariantAvailable && cartAddonSelected) {
        acceleratedCheckoutNotice.removeAttribute('hidden');
      } else {
        acceleratedCheckoutNotice.setAttribute('hidden', 'true');
      }
    }
  }

  /**
   * Collects checkbox-based linked add-ons selected on the form.
   * @param {HTMLFormElement} form
   * @returns {Array<{
   *   type: string,
   *   title: string,
   *   handle: string,
   *   explicitVariantId: number | null,
   *   variantIdProperty: string
   * }>}
   */
  #collectSelectedLinkedAddons(form) {
    const addonInputs = /** @type {NodeListOf<HTMLInputElement>} */ (form.querySelectorAll('[data-linked-addon-quantity]'));

    return Array.from(addonInputs)
      .map((input) => ({
        units: Math.max(0, Number(input.value) || 0),
        type: `${input.dataset.addonType || ''}`.trim(),
        title: `${input.dataset.addonTitle || ''}`.trim(),
        handle: `${input.dataset.addonHandle || ''}`.trim(),
        explicitVariantId: (() => {
          const variantId = Number(input.dataset.addonVariantId || '');
          return Number.isFinite(variantId) && variantId > 0 ? variantId : null;
        })(),
        variantIdProperty: `${input.dataset.addonVariantIdProperty || ''}`.trim(),
      }))
      .filter((addon) => addon.units > 0 && addon.type && addon.title && addon.variantIdProperty);
  }

  /**
   * Handles linked add-on quantity stepper clicks.
   * @param {MouseEvent} event
   */
  #onAddonStepperClick = (event) => {
    const button = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('[data-linked-addon-adjust]') : null);
    if (!button) return;

    const targetId = `${button.dataset.linkedAddonTarget || ''}`.trim();
    const stepDelta = Number(button.dataset.linkedAddonAdjust || '0');
    if (!targetId || !Number.isFinite(stepDelta) || stepDelta === 0) return;

    const input = /** @type {HTMLInputElement | null} */ (this.querySelector(`#${CSS.escape(targetId)}`));
    if (!input) return;

    const currentValue = Math.max(0, Number(input.value) || 0);
    const nextValue = Math.max(0, currentValue + stepDelta);
    if (nextValue === currentValue) return;

    input.value = `${nextValue}`;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Shows or hides linked add-on selection fields based on quantity.
   */
  #syncLinkedAddonSelectionFields() {
    const selectionWrappers = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('[data-linked-addon-selection-wrapper]')
    );

    selectionWrappers.forEach((wrapper) => {
      const targetId = `${wrapper.dataset.linkedAddonTarget || ''}`.trim();
      if (!targetId) return;

      const quantityInput = /** @type {HTMLInputElement | null} */ (this.querySelector(`#${CSS.escape(targetId)}`));
      const selectionInput = /** @type {HTMLInputElement | HTMLTextAreaElement | null} */ (
        wrapper.querySelector('[data-linked-addon-selection-input]')
      );
      if (!quantityInput || !selectionInput) return;

      const shouldShow = (Number(quantityInput.value) || 0) > 0;
      wrapper.toggleAttribute('hidden', !shouldShow);
      selectionInput.disabled = !shouldShow;

      if (!shouldShow) {
        selectionInput.value = '';
      }
    });
  }

  /**
   * Shows or hides engraving detail fields based on checkbox state.
   */
  #syncEngravingDetailFields() {
    const engravingOptInInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-engraving-opt-in]'));
    const engravingDetails = /** @type {HTMLElement | null} */ (this.querySelector('[data-engraving-details]'));
    if (!engravingDetails) return;

    const shouldShow = Boolean(engravingOptInInput?.checked);
    engravingDetails.toggleAttribute('hidden', !shouldShow);

    const detailInputs = /** @type {NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>} */ (
      engravingDetails.querySelectorAll('[data-engraving-detail-input]')
    );

    detailInputs.forEach((input) => {
      input.disabled = !shouldShow;

      if (!shouldShow) {
        input.value = '';
      }
    });
  }

  /**
   * Parses comma-separated add-on codes.
   * @param {string} value
   * @returns {string[]}
   */
  #parseAddonSelectionValues(value) {
    return `${value || ''}`
      .split(/[\n,;]+/)
      .map((entry) => entry.trim().toUpperCase())
      .filter(Boolean);
  }

  /**
   * Resolves a standard linked add-on variant by handle first, title second, and explicit ID last.
   * @param {{ explicitId?: number | null, handle?: string, title?: string }} config
   * @returns {Promise<number | null>}
   */
  async #resolveLinkedAddonVariantId(config = {}) {
    const explicitId = Number(config.explicitId || '');
    const handle = `${config.handle || ''}`.trim().toLowerCase();
    const title = `${config.title || ''}`.trim().toLowerCase();
    const explicitCacheKey = Number.isFinite(explicitId) && explicitId > 0 ? `id:${explicitId}` : '';

    if (handle && linkedAddonVariantCache.has(`handle:${handle}`)) {
      return linkedAddonVariantCache.get(`handle:${handle}`);
    }

    if (handle) {
      try {
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
        if (response.ok) {
          const addonProduct = await response.json();
          const addonVariant = addonProduct?.variants?.find((variant) => variant?.available) ?? addonProduct?.variants?.[0];
          const variantId = Number(addonVariant?.id || '');
          const resolvedId = Number.isFinite(variantId) && variantId > 0 ? variantId : null;
          linkedAddonVariantCache.set(`handle:${handle}`, resolvedId);
          if (title) {
            linkedAddonVariantCache.set(`title:${title}`, resolvedId);
          }
          if (resolvedId) return resolvedId;
        } else {
          linkedAddonVariantCache.set(`handle:${handle}`, null);
        }
      } catch (error) {
        console.error('Failed to resolve linked add-on by handle:', handle, error);
        linkedAddonVariantCache.set(`handle:${handle}`, null);
      }
    }

    if (title && linkedAddonVariantCache.has(`title:${title}`)) {
      return linkedAddonVariantCache.get(`title:${title}`);
    }

    if (!title) return null;

    try {
      const response = await fetch('/products.json?limit=250');
      if (!response.ok) {
        linkedAddonVariantCache.set(`title:${title}`, null);
        return null;
      }

      const data = await response.json();
      const products = Array.isArray(data?.products) ? data.products : [];
      const addonProduct = products.find((product) => `${product?.title || ''}`.trim().toLowerCase() === title);
      const addonVariant = addonProduct?.variants?.find((variant) => variant?.available) ?? addonProduct?.variants?.[0];
      const variantId = Number(addonVariant?.id || '');
      const resolvedId = Number.isFinite(variantId) && variantId > 0 ? variantId : null;

      linkedAddonVariantCache.set(`title:${title}`, resolvedId);
      if (addonProduct?.handle) {
        linkedAddonVariantCache.set(`handle:${`${addonProduct.handle}`.trim().toLowerCase()}`, resolvedId);
      }

      return resolvedId;
    } catch (error) {
      console.error('Failed to resolve linked add-on by title:', title, error);
      linkedAddonVariantCache.set(`title:${title}`, null);
    }

    if (!Number.isFinite(explicitId) || explicitId <= 0) {
      return null;
    }

    if (explicitCacheKey && linkedAddonVariantCache.has(explicitCacheKey)) {
      return linkedAddonVariantCache.get(explicitCacheKey);
    }

    try {
      const response = await fetch('/products.json?limit=250');
      if (response.ok) {
        const data = await response.json();
        const products = Array.isArray(data?.products) ? data.products : [];
        const addonProduct = products.find((product) => {
          const productId = Number(product?.id || '');
          if (productId === explicitId) return true;

          return Array.isArray(product?.variants)
            ? product.variants.some((variant) => Number(variant?.id || '') === explicitId)
            : false;
        });

        if (addonProduct) {
          const exactVariant = addonProduct.variants?.find((variant) => Number(variant?.id || '') === explicitId) || null;
          const addonVariant =
            exactVariant ||
            addonProduct.variants?.find((variant) => variant?.available) ||
            addonProduct.variants?.[0];
          const variantId = Number(addonVariant?.id || '');
          const resolvedId = Number.isFinite(variantId) && variantId > 0 ? variantId : null;

          if (explicitCacheKey) {
            linkedAddonVariantCache.set(explicitCacheKey, resolvedId);
          }
          if (title) {
            linkedAddonVariantCache.set(`title:${title}`, resolvedId);
          }
          if (addonProduct?.handle) {
            linkedAddonVariantCache.set(`handle:${`${addonProduct.handle}`.trim().toLowerCase()}`, resolvedId);
          }

          return resolvedId;
        }
      }
    } catch (error) {
      console.error('Failed to resolve linked add-on by explicit id:', explicitId, error);
    }

    if (explicitCacheKey) {
      linkedAddonVariantCache.set(explicitCacheKey, null);
    }
    return null;
  }

  /**
   * Resolves the engraving add-on variant ID.
   * @returns {Promise<number | null>}
   */
  async #resolveEngravingAddonVariantId() {
    const explicitVariantId = Number(this.dataset.engravingAddonVariantId || '');
    if (Number.isFinite(explicitVariantId) && explicitVariantId > 0) {
      return explicitVariantId;
    }

    const configuredHandle = (this.dataset.engravingAddonHandle || ENGRAVING_ADDON_DEFAULT_HANDLE).trim().toLowerCase();
    const handleCandidates = [...new Set([configuredHandle, ...ENGRAVING_ADDON_FALLBACK_HANDLES])].filter(Boolean);

    for (const addonHandle of handleCandidates) {
      if (engravingAddonVariantCache.has(addonHandle)) {
        const cachedVariantId = engravingAddonVariantCache.get(addonHandle);
        if (cachedVariantId) return cachedVariantId;
        continue;
      }

      try {
        const response = await fetch(`/products/${encodeURIComponent(addonHandle)}.js`);
        if (!response.ok) {
          engravingAddonVariantCache.set(addonHandle, null);
          continue;
        }

        const addonProduct = await response.json();
        const addonVariant = addonProduct?.variants?.find((variant) => variant.available) ?? addonProduct?.variants?.[0];
        const addonVariantId = Number(addonVariant?.id || '');
        const resolvedId = Number.isFinite(addonVariantId) && addonVariantId > 0 ? addonVariantId : null;

        engravingAddonVariantCache.set(addonHandle, resolvedId);
        if (resolvedId) return resolvedId;
      } catch (error) {
        console.error('Failed to resolve engraving add-on variant for handle:', addonHandle, error);
        engravingAddonVariantCache.set(addonHandle, null);
      }
    }

    if (engravingAddonVariantCache.has(ENGRAVING_ADDON_CACHE_KEY)) {
      return engravingAddonVariantCache.get(ENGRAVING_ADDON_CACHE_KEY);
    }

    try {
      const response = await fetch('/products.json?limit=250');
      if (!response.ok) {
        engravingAddonVariantCache.set(ENGRAVING_ADDON_CACHE_KEY, null);
        return null;
      }

      const data = await response.json();
      const products = Array.isArray(data?.products) ? data.products : [];
      const engravingProduct = products.find((product) => {
        const handle = `${product?.handle || ''}`.toLowerCase();
        const title = `${product?.title || ''}`.toLowerCase();
        return handle.includes('engraving') || title.includes('engraving');
      });

      const addonVariant =
        engravingProduct?.variants?.find((variant) => variant?.available) ?? engravingProduct?.variants?.[0];
      const addonVariantId = Number(addonVariant?.id || '');
      const resolvedId = Number.isFinite(addonVariantId) && addonVariantId > 0 ? addonVariantId : null;

      if (engravingProduct?.handle) {
        engravingAddonVariantCache.set(`${engravingProduct.handle}`.trim().toLowerCase(), resolvedId);
      }
      engravingAddonVariantCache.set(ENGRAVING_ADDON_CACHE_KEY, resolvedId);
      return resolvedId;
    } catch (error) {
      console.error('Failed to resolve engraving add-on variant via product lookup:', error);
      engravingAddonVariantCache.set(ENGRAVING_ADDON_CACHE_KEY, null);
      return null;
    }
  }

  /**
   * Adds the engraving upcharge item to cart.
   * @param {number} variantId
   * @param {number} quantity
   * @param {string} sectionIds
   * @param {string | null} parentVariantId
   * @returns {Promise<any>}
   */
  async #addEngravingAddonToCart(variantId, quantity, sectionIds, parentVariantId) {
    const requestBody = {
      items: [
        {
          id: variantId,
          quantity,
          properties: {
            _engraving_addon: 'true',
            _engraving_for_variant: parentVariantId || '',
          },
        },
      ],
    };

    if (sectionIds) {
      requestBody.sections = sectionIds;
    }

    const fetchCfg = fetchConfig('javascript');
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();
    if (data.status) {
      throw new Error(data.message || data.description || 'Unable to add engraving add-on');
    }

    return data;
  }

  /**
   * Adds a linked product add-on to the cart.
   * @param {number} variantId
   * @param {number} quantity
   * @param {string} sectionIds
   * @param {string | null} parentVariantId
   * @param {string} addonType
   * @param {number} unitsPerParent
   * @returns {Promise<any>}
   */
  async #addLinkedAddonToCart(variantId, quantity, sectionIds, parentVariantId, addonType, unitsPerParent) {
    const requestBody = {
      items: [
        {
          id: variantId,
          quantity,
          properties: {
            _linked_addon: 'true',
            _linked_addon_type: addonType,
            _linked_addon_for_variant: parentVariantId || '',
            _linked_addon_unit_count: `${unitsPerParent}`,
          },
        },
      ],
    };

    if (sectionIds) {
      requestBody.sections = sectionIds;
    }

    const fetchCfg = fetchConfig('javascript');
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();
    if (data.status) {
      throw new Error(data.message || data.description || 'Unable to add linked add-on');
    }

    return data;
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Responds to product form state changes such as engraving selection.
   */
  #onFormStateChange = () => {
    this.#syncEngravingDetailFields();
    this.#syncLinkedAddonSelectionFields();
    const addToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    const isVariantAvailable = Boolean(this.refs.variantId?.value) && Boolean(addToCartButton && !addToCartButton.disabled);
    this.#syncAcceleratedCheckoutState(isVariantAvailable);
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        this.#showFormError(errorMessage);

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);
    const requestedQuantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const lineItemQuantity = Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? requestedQuantity : 1;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    const sectionIds = cartItemComponentsSectionIds.join(',');
    if (sectionIds) {
      formData.append('sections', sectionIds);
    }

    const engravingOptInInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-engraving-opt-in]'));
    const wantsEngraving = Boolean(engravingOptInInput?.checked);
    const selectedLinkedAddons = this.#collectSelectedLinkedAddons(form);
    const linkedAddonQuantityInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      form.querySelectorAll('[data-linked-addon-quantity]')
    );
    const engravingTextKey = 'properties[Engraving text]';
    const engravingFinishKey = 'properties[Engraving finish]';
    const engravingAddonVariantIdKey = 'properties[_engraving_addon_variant_id]';
    const engravingTextValue = formData.get(engravingTextKey)?.toString().trim() || '';
    const engravingFinishValue = formData.get(engravingFinishKey)?.toString().trim() || '';

    if (!wantsEngraving || !engravingTextValue) {
      formData.delete(engravingTextKey);
    } else {
      formData.set(engravingTextKey, engravingTextValue);
    }

    if (!wantsEngraving || !engravingFinishValue) {
      formData.delete(engravingFinishKey);
    } else {
      formData.set(engravingFinishKey, engravingFinishValue);
    }

    let engravingAddonVariantId = null;
    /** @type {Array<{ type: string, title: string, variantId: number, units: number }>} */
    const resolvedLinkedAddons = [];

    if (wantsEngraving) {
      if (!engravingTextValue) {
        this.#showFormError('Enter embossing text before adding personalized embossing.');
        return;
      }

      if (!engravingFinishValue) {
        this.#showFormError('Choose an embossing finish before adding personalized embossing.');
        return;
      }

      engravingAddonVariantId = await this.#resolveEngravingAddonVariantId();
      if (!engravingAddonVariantId) {
        this.#showFormError(
          'Embossing add-on is unavailable right now. Please try again in a moment.'
        );
        return;
      }

      formData.set(engravingAddonVariantIdKey, `${engravingAddonVariantId}`);
    } else {
      formData.delete(engravingAddonVariantIdKey);
    }

    linkedAddonQuantityInputs.forEach((input) => {
      const inputName = `${input.name || ''}`.trim();
      if (!inputName) return;

      const selectedUnits = Math.max(0, Number(input.value) || 0);
      if (selectedUnits > 0) {
        formData.set(inputName, `${selectedUnits}`);
      } else {
        formData.delete(inputName);
      }
    });

    for (const [addonType, rule] of Object.entries(LINKED_ADDON_SELECTION_RULES)) {
      const quantityInput = /** @type {HTMLInputElement | null} */ (
        form.querySelector(`[data-linked-addon-quantity][data-addon-type="${addonType}"]`)
      );
      const selectedUnits = Math.max(0, Number(quantityInput?.value) || 0);
      const rawSelectionValue = formData.get(rule.propertyName)?.toString().trim() || '';

      if (selectedUnits === 0) {
        formData.delete(rule.propertyName);
        continue;
      }

      if (!rawSelectionValue) {
        this.#showFormError(`Enter ${rule.label.toLowerCase()} before adding this journal to cart.`);
        return;
      }

      const parsedSelections = this.#parseAddonSelectionValues(rawSelectionValue);
      if (parsedSelections.length !== selectedUnits) {
        this.#showFormError(
          `Enter ${selectedUnits} ${rule.label.toLowerCase()} code${selectedUnits === 1 ? '' : 's'} to match the quantity selected.`
        );
        return;
      }

      const hasInvalidSelection = parsedSelections.some((value) => !rule.allowedValues.includes(value));
      if (hasInvalidSelection) {
        this.#showFormError(`${rule.label} must use only these codes: ${rule.allowedValues.join(', ')}.`);
        return;
      }

      formData.set(rule.propertyName, parsedSelections.join(', '));
    }

    for (const addon of selectedLinkedAddons) {
      const variantId = await this.#resolveLinkedAddonVariantId({
        explicitId: addon.explicitVariantId,
        handle: addon.handle,
        title: addon.title,
      });

      if (!variantId) {
        this.#showFormError(`${addon.title} is unavailable right now. Please try again in a moment.`);
        return;
      }

      formData.set(addon.variantIdProperty, `${variantId}`);
      resolvedLinkedAddons.push({
        type: addon.type,
        title: addon.title,
        variantId,
        units: addon.units,
      });
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          this.#showFormError(response.message);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: lineItemQuantity,
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          let updatedSections = response.sections;
          if (wantsEngraving && engravingAddonVariantId) {
            try {
              const engravingResponse = await this.#addEngravingAddonToCart(
                engravingAddonVariantId,
                lineItemQuantity,
                sectionIds,
                id.toString()
              );

              if (engravingResponse?.sections) {
                updatedSections = engravingResponse.sections;
              }
            } catch (error) {
              console.error(error);
              this.#showFormError(
                'Main item was added, but the embossing charge could not be added. Please remove the item from cart and try again.'
              );
            }
          }

          for (const addon of resolvedLinkedAddons) {
            try {
              const addonResponse = await this.#addLinkedAddonToCart(
                addon.variantId,
                lineItemQuantity * addon.units,
                sectionIds,
                id.toString(),
                addon.type,
                addon.units
              );

              if (addonResponse?.sections) {
                updatedSections = addonResponse.sections;
              }
            } catch (error) {
              console.error(error);
              this.#showFormError(
                `Main item was added, but ${addon.title} could not be added. Please remove the item from cart and try again.`
              );
            }
          }

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: lineItemQuantity,
              productId: this.dataset.productId,
              sections: updatedSections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';
    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    this.#syncAcceleratedCheckoutState(Boolean(event.detail.resource?.available));

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    this.#syncAcceleratedCheckoutState(Boolean(event.detail.resource?.available));

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
